import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { inventoryErrorKey } from './inventoryErrors';
import { Caution, Dialog, ErrorNote, Field, INPUT, PRIMARY_BUTTON } from './registerUi';
import {
  useActivateSim,
  useCreateSim,
  useUpdateSim,
  type AdminSim,
  type SimStatus,
} from './useInventory';

const SIM_STATUSES: readonly SimStatus[] = ['in_service', 'retired', 'lost'];

/**
 * Register a SIM — ICCID required, number optional.
 *
 * 🔴 THE ICCID IS THE REQUIRED FIELD AND THE MSISDN IS NOT, WHICH IS THE WHOLE
 * SHAPE OF THIS REGISTER. The ICCID is printed on the plastic and exists the
 * moment the SIM does; the number is issued by the carrier later, sometimes days
 * later. A form demanding both would leave a box of real SIMs unrecorded until
 * paperwork caught up — and unrecorded stock is exactly what this slice removes.
 *
 * A number typed here is normalised to E.164 SERVER-side, in the same place
 * `users.phone` is normalised, so a SIM number and a user's phone can be
 * compared. A malformed one comes back as `invalid_body`.
 */
export function NewSimDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateSim();
  const [iccid, setIccid] = useState('');
  const [carrier, setCarrier] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [apn, setApn] = useState('');
  const [notes, setNotes] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function submit() {
    setErrorKey(null);
    try {
      await create.mutateAsync({
        iccid: iccid.trim(),
        carrier: carrier.trim(),
        msisdn: msisdn.trim() || null,
        apn: apn.trim() || null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('inventory.sims.new')}
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={iccid.trim().length < 6 || carrier.trim() === '' || create.isPending}
          onClick={() => void submit()}
        >
          {create.isPending ? t('inventory.saving') : t('inventory.sims.register')}
        </button>
      }
    >
      <div className="grid gap-4">
        <Field label={t('inventory.sims.iccid')} hint={t('inventory.sims.iccidHint')}>
          <input
            className={INPUT}
            value={iccid}
            onChange={(e) => setIccid(e.target.value)}
            inputMode="numeric"
            autoFocus
          />
        </Field>
        <Field label={t('inventory.sims.carrier')}>
          <input
            className={INPUT}
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            placeholder="Hormuud"
          />
        </Field>
        <Field label={t('inventory.sims.msisdn')} hint={t('inventory.sims.msisdnOptionalHint')}>
          <input
            className={INPUT}
            value={msisdn}
            onChange={(e) => setMsisdn(e.target.value)}
            inputMode="tel"
          />
        </Field>
        <Field label={t('inventory.sims.apn')}>
          <input className={INPUT} value={apn} onChange={(e) => setApn(e.target.value)} />
        </Field>
        <Field label={t('inventory.notes')}>
          <input className={INPUT} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}

/** Carrier, APN, condition, notes. Never the ICCID and never the number. */
export function EditSimDialog({ sim, onClose }: { sim: AdminSim; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateSim();
  const [carrier, setCarrier] = useState(sim.carrier);
  const [apn, setApn] = useState(sim.apn ?? '');
  const [status, setStatus] = useState<SimStatus>(sim.status);
  const [notes, setNotes] = useState(sim.notes ?? '');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function submit() {
    setErrorKey(null);
    try {
      await update.mutateAsync({
        simId: sim.id,
        body: {
          carrier: carrier.trim(),
          apn: apn.trim() || null,
          status,
          notes: notes.trim() || null,
        },
      });
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('inventory.sims.edit', { iccid: sim.iccid })}
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={carrier.trim() === '' || update.isPending}
          onClick={() => void submit()}
        >
          {update.isPending ? t('inventory.saving') : t('inventory.save')}
        </button>
      }
    >
      <div className="grid gap-4">
        {/* Both identities, shown and not editable. A past assignment resolves
            through them, so editing either would rewrite what the past says —
            and neither field exists on the PATCH schema. */}
        <p className="text-[12.5px] text-raad-muted">
          {t('inventory.sims.iccid')}: <span className="num text-raad-text">{sim.iccid}</span>
        </p>
        <p className="text-[12.5px] text-raad-muted">
          {t('inventory.sims.msisdn')}:{' '}
          {sim.msisdn === null ? (
            <span className="text-raad-warn">{t('inventory.sims.notActivated')}</span>
          ) : (
            <span className="num text-raad-text">{sim.msisdn}</span>
          )}
        </p>
        <Field label={t('inventory.sims.carrier')}>
          <input className={INPUT} value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        </Field>
        <Field label={t('inventory.sims.apn')}>
          <input className={INPUT} value={apn} onChange={(e) => setApn(e.target.value)} />
        </Field>
        <Field label={t('inventory.status')}>
          <select
            className={INPUT}
            value={status}
            onChange={(e) => setStatus(e.target.value as SimStatus)}
          >
            {SIM_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`inventory.simStatus.${s}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('inventory.notes')}>
          <input className={INPUT} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}

/**
 * The carrier issued a number.
 *
 * 🔴 ONE-WAY, AND SAID SO ON THE SCREEN. The server refuses a SIM that already
 * carries a number with `sim_already_activated` rather than overwriting it: a
 * number is identity, and a past assignment resolves through it. A typo here is
 * not fixable from this screen, which is why the dialog says so BEFORE the
 * button rather than explaining it in a 409 afterwards.
 */
export function ActivateSimDialog({ sim, onClose }: { sim: AdminSim; onClose: () => void }) {
  const { t } = useTranslation();
  const activate = useActivateSim();
  const [msisdn, setMsisdn] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function submit() {
    setErrorKey(null);
    try {
      await activate.mutateAsync({ simId: sim.id, msisdn: msisdn.trim() });
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('inventory.sims.activateTitle', { iccid: sim.iccid })}
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={msisdn.trim().length < 6 || activate.isPending}
          onClick={() => void submit()}
        >
          {activate.isPending ? t('inventory.saving') : t('inventory.sims.activate')}
        </button>
      }
    >
      <div className="grid gap-4">
        <Caution>{t('inventory.sims.activateOnceWarning')}</Caution>
        <Field label={t('inventory.sims.msisdn')} hint={t('inventory.sims.msisdnHint')}>
          <input
            className={INPUT}
            value={msisdn}
            onChange={(e) => setMsisdn(e.target.value)}
            inputMode="tel"
            autoFocus
          />
        </Field>
      </div>
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}
